import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { AttendeesIcon } from 'components/icons/AttendeesIcon';
import { ExhibitorsIcon } from 'components/icons/ExhibitorsIcon';
import { OrganizersIcon } from 'components/icons/OrganizersIcon';
import { SpeakersIcon } from 'components/icons/SpeakersIcon';
import { SponsorsIcon } from 'components/icons/SponsorsIcon';
import { VendorsIcon } from 'components/icons/VendorsIcon';
import { Button } from 'components/ui/Button';
import { Dialog, DialogContent } from 'components/ui/Dialog';
import { Input } from 'components/ui/Input';
import { SectionLabel } from 'components/ui/SectionLabel';
import { Toast } from 'components/ui/Toast';
import { BackendRoute } from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { ReactNode, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import apiService from 'services/api.service';
import { HttpException } from 'services/http.exception';
import { joinWaitlistSchema } from 'validations';
import z from 'zod';

interface BestSuitedFixture {
  title: StringKey;
  icon: ReactNode;
}

const bestSuitedFixtures: BestSuitedFixture[] = [
  { title: StringKey.ORGANIZERS, icon: <OrganizersIcon /> },
  { title: StringKey.VENDORS, icon: <VendorsIcon /> },
  { title: StringKey.SPONSORS, icon: <SponsorsIcon /> },
  { title: StringKey.EXHIBITORS, icon: <ExhibitorsIcon /> },
  { title: StringKey.SPEAKERS, icon: <SpeakersIcon /> },
  { title: StringKey.ATTENDEES, icon: <AttendeesIcon /> },
];

export const JoinWaitlistSection = () => {
  const { t, direction } = useLanguage();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const form = useForm<z.infer<typeof joinWaitlistSchema>>({
    resolver: zodResolver(joinWaitlistSchema),
  });

  const joinWaitlist = useCallback(async (data: z.infer<typeof joinWaitlistSchema>) => {
    await apiService.post(
      {
        endpoint: BackendRoute.JOIN_WAITLIST,
      },
      {
        body: { ...data },
      },
    );
  }, []);

  const joinWaitlistMutation = useMutation({
    mutationFn: joinWaitlist,
    onSuccess: () => {
      setIsModalOpen(true);
      form.reset();
    },
    onError: (error) => {
      toast(
        <Toast
          type="error"
          title={
            error instanceof HttpException && error.body?.message?.toLowerCase().includes('request')
              ? t(StringKey.TOAST_ERROR_TITLE)
              : t(StringKey.YOU_ARE_ALL_SET_MSG)
          }
          description={
            error instanceof HttpException
              ? error.body?.statusCode === 429
                ? t(StringKey.TOO_MANY_REQUESTS_MSG)
                : error.body?.message
              : t(StringKey.TOAST_ERROR_DESC)
          }
        />,
        {
          className: 'p-0 w-[calc(100%-32px)] mx-4 xs:m-0 xs:w-[400px] bg-transparent',
        },
      );
    },
  });

  const onSubmit = (data: z.infer<typeof joinWaitlistSchema>) => {
    joinWaitlistMutation.mutate({ ...data });
  };

  const emailErrorMsg = form.formState.errors.email?.message;

  return (
    <section className="relative w-full overflow-x-hidden bg-[#000006]">
      <div className="relative px-20 py-24 max-md:px-4 max-md:py-14">
        <div className="relative z-[2] px-8 py-[90px] flex flex-col gap-12 max-md:p-0">
          <div className="relative lg:w-[65%] flex flex-col gap-6">
            <div className="flex flex-col gap-4 max-md:items-center">
              <SectionLabel>{t(StringKey.THE_PLACE_IS_YOURS)}</SectionLabel>
              <h1 className="bg-clip-text bg-label-gradient text-transparent font-semibold text-[60px] leading-[72px] max-md:text-[36px] max-md:leading-[44px] max-md:text-center">
                {t(StringKey.JOIN_WAITLIST_TITLE)}
              </h1>
            </div>
            <p className="text-[20px] leading-[30px] max-md:text-[16px] max-md:leading-6 max-md:text-center max-md:font-normal">
              {t(StringKey.JOIN_WAITLIST_DESC)}
            </p>
          </div>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="relative flex items-center gap-3 max-md:flex-col"
          >
            <div
              className={cn(
                'relative w-full md:max-w-[370px] lg:max-w-[400px] h-12 md:h-14 bg-[linear-gradient(180deg,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0.11)_100%)] rounded-lg p-[1px]',
                emailErrorMsg && 'bg-error',
              )}
            >
              <Input
                dir="ltr"
                {...form.register('email')}
                placeholder={t(StringKey.YOUR_EMAIL)}
                errorMessage={emailErrorMsg}
                className={cn(
                  'w-full h-[45.5px] md:h-[54px] border-0 ring-0 p-4 bg-[linear-gradient(144.42deg,#171221_0%,#0B0910_100%)] rounded-lg',
                  direction === 'rtl' ? 'text-right' : 'text-left',
                )}
              />
            </div>
            <Button
              type="submit"
              className={cn('max-md:w-full h-12 md:h-14', emailErrorMsg && 'max-md:mt-6')}
            >
              {t(StringKey.JOIN_WAITLIST)}
            </Button>
          </form>
          <div className="flex items-center gap-3 text-[14px] leading-[20px] flex-wrap max-md:justify-center">
            <div className="uppercase text-[#616161]">{t(StringKey.BEST_SUITED)}</div>
            {bestSuitedFixtures.map((item) => (
              <div key={item.title} className="flex items-center gap-1.5">
                {item.icon}
                <div>{t(item.title)}</div>
              </div>
            ))}

            <Dialog open={isModalOpen} onOpenChange={() => setIsModalOpen(!isModalOpen)}>
              <DialogContent className="p-0 gap-0 w-[calc(100%-32px)] sm:w-[calc(100%-64px)] md:w-1/2">
                <div className="text-center">
                  <div className="p-5 md:p-8 flex flex-col gap-6">
                    <h1 className="bg-clip-text bg-label-gradient text-transparent font-semibold text-[30px] leading-[38px] md:text-[36px] md:leading-[44px]">
                      {t(StringKey.WAITLIST_MODAL_TITLE)}
                    </h1>
                    <div className="text-m leading-6 md:text-xl md:leading-[30px]">
                      {t(StringKey.WAITLIST_MODAL_DESC)}
                    </div>
                  </div>
                  <img
                    className="w-full h-6 block object-cover"
                    src="/assets/form-divider.png"
                    alt="form-divider"
                  />
                  <div className="p-5 md:p-8">
                    <Button className="w-full" onClick={() => setIsModalOpen(false)}>
                      {t(StringKey.GOT_IT)}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div
        className={cn('absolute z-[1] h-full top-0', direction === 'rtl' ? 'left-0' : 'right-0')}
      >
        <img
          className="h-full object-cover"
          src={`/assets/door-bg${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="Door background"
        />
      </div>
    </section>
  );
};
